<template>
  <div>
    <v-row class="mb-3">
      <v-col cols="12" md="4">
        <v-text-field
          v-model="searchQuery"
          label="Search networks..."
          prepend-inner-icon="mdi-magnify"
          clearable
          dense
          outlined
          hide-details
        />
      </v-col>
      <v-col cols="12" md="3">
        <v-select
          :items="publicOptions"
          label="Status"
          :value="is_public ? is_public : ''"
          item-text="label"
          item-value="value"
          dense
          outlined
          hide-details
          @change="onPublicFilterChange"
        />
      </v-col>
      <v-col cols="auto" class="ml-auto">
        <v-btn color="primary" @click="openNetworkDialog()">
          <v-icon left>mdi-plus</v-icon> Add Network
        </v-btn>
      </v-col>
    </v-row>

    <v-data-table
      :headers="headers"
      :items="networks"
      :loading="loading"
      disable-sort
      hide-default-footer
      :items-per-page="-1"
    >
      <template v-slot:item.pixel_id_param="{ item }">
        {{ item.pixel_id_param || "—" }}
      </template>

      <template v-slot:item.is_public="{ item }">
        <v-chip :color="item.is_public ? 'green' : 'grey'" small dark>
          {{ item.is_public ? "Yes" : "No" }}
        </v-chip>
      </template>

      <template v-slot:item.mapping="{ item }">
        <v-btn
          v-if="item.mapping && item.mapping.id"
          x-small
          color="warning"
          outlined
          @click="openMappingDialog(item)"
        >
          Edit
        </v-btn>
        <v-btn
          v-else
          x-small
          color="success"
          outlined
          @click="openMappingDialog(item)"
        >
          Create
        </v-btn>
      </template>

      <template v-slot:item.actions="{ item }">
        <v-btn icon small @click="openNetworkDialog(item)">
          <v-icon small>mdi-pencil</v-icon>
        </v-btn>
        <v-btn
          :loading="deleteLoading"
          icon
          small
          color="red"
          @click="deleteNetwork(item)"
        >
          <v-icon small>mdi-delete</v-icon>
        </v-btn>
      </template>
    </v-data-table>

    <network-dialog
      :visible="networkDialogVisible"
      :network="selectedNetwork"
      @close="
        () => {
          networkDialogVisible = false;
          selectedNetwork = null;
        }
      "
      @saved="onNetworkSaved"
    />

    <mapping-dialog
      :visible="mappingDialogVisible"
      :network="selectedNetwork"
      :mappingId="selectedMappingId"
      @close="
        () => {
          mappingDialogVisible = false;
          selectedNetwork = null;
          selectedMappingId = null;
        }
      "
      @saved="onMappingSaved"
    />
  </div>
</template>

<script>
import {
  getNetworks,
  deleteNetwork as deleteNetworkApi,
} from "@/store/modules/advertisers/api";
import { requestStatus, buildFilterString } from "@/services/services";
import { mapMutations, mapState } from "vuex";
import { CHANGE_FILTER } from "@/store/modules/page/types";
import NetworkDialog from "./NetworkDialog";
import MappingDialog from "./MappingDialog";
import { debounce } from "lodash";

export default {
  name: "NetworksTable",
  components: { NetworkDialog, MappingDialog },
  data: () => ({
    headers: [
      { text: "Name", value: "name" },
      { text: "Slug", value: "slug", width: "120px" },
      { text: "Visit ID Param", value: "visit_id_param", width: "150px" },
      { text: "Pixel ID Param", value: "pixel_id_param", width: "150px" },
      { text: "Public", value: "is_public", width: "90px" },
      { text: "Mapping", value: "mapping", width: "120px", sortable: false },
      { text: "Actions", value: "actions", width: "120px", sortable: false },
    ],
    networks: [],
    loading: false,
    networkDialogVisible: false,
    mappingDialogVisible: false,
    selectedNetwork: null,
    selectedMappingId: null,
    deleteLoading: false,
    searchQuery: "",
    publicOptions: [
      {
        label: "All",
        value: null,
      },
      {
        label: "Public",
        value: "true",
      },
      {
        label: "Private",
        value: "false",
      },
    ],
  }),
  computed: {
    ...mapState({
      pageFilter: (state) => state.page.filter,
      is_public: (state) => state.page.filter.is_public,
      search: (state) => state.page.filter.search,
    }),
  },
  mounted() {
    this.searchQuery = this.search;
    if (!this.search) this.fetchNetworks();
  },
  methods: {
    ...mapMutations({
      changeFilter: `page/${CHANGE_FILTER}`,
    }),

    onPublicFilterChange(is_public) {
      this.changeFilter({ is_public: is_public === null ? "" : is_public });
    },

    fetchNetworks() {
      this.loading = true;
      const filterString = buildFilterString(this.pageFilter);
      getNetworks(filterString)
        .then((response) => {
          if (response.status === requestStatus.success) {
            this.networks = response.response.data.results || [];
          } else {
            this.$toaster.error(response.messages || "Failed to load networks");
          }
        })
        .finally(() => (this.loading = false));
    },

    onWaitSearchChange: debounce(function () {
      if (this.searchQuery === null) this.searchQuery = "";
      if (
        (this.searchQuery || this.searchQuery === "") &&
        (this.searchQuery.length >= 2 || this.searchQuery === "")
      ) {
        this.changeFilter({ search: this.searchQuery });
      }
    }, 1000),

    openNetworkDialog(network = null) {
      this.selectedNetwork = network;
      this.networkDialogVisible = true;
    },

    openMappingDialog(network) {
      this.selectedNetwork = network;
      this.selectedMappingId = network.mapping ? network.mapping.id : null;
      this.mappingDialogVisible = true;
    },

    deleteNetwork(network) {
      window.getApp.$refs.global_confirm_modal
        .open("Confirm remove network")
        .then((confirm) => {
          if (confirm) {
            this.deleteLoading = true;
            deleteNetworkApi(network.id)
              .then((response) => {
                if (response.status === requestStatus.success) {
                  this.$toaster.success("Network deleted successfully");
                  this.fetchNetworks();
                } else {
                  this.$toaster.error(
                    response.messages || "Failed to delete network",
                  );
                }
              })
              .finally(() => (this.deleteLoading = false));
          }
        });
    },

    onNetworkSaved() {
      this.selectedNetwork = null;
      this.fetchNetworks();
      this.networkDialogVisible = false;
    },

    onMappingSaved() {
      this.fetchNetworks();
      this.mappingDialogVisible = false;
      this.selectedMappingId = null;
      this.selectedNetwork = null;
    },
  },
  watch: {
    searchQuery() {
      this.onWaitSearchChange();
    },
    pageFilter() {
      this.fetchNetworks();
    },
  },
};
</script>

<style scoped></style>
